import { computed, inject, Injectable, Injector, Signal, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { select } from '@ngxs/store';
import { filter, map } from 'rxjs';
import { Country, IUser } from '@shs-digi-care-monorepo/interfaces';
import { UserState } from '@shs-digi-care-monorepo/frontend-lib';
import { emeaCountriesOf, emeaTaskOwnerCountriesOf } from '../utils/emea-countries.util';
import { EmeaFeature, EmeaFeatureService } from './emea-feature.service';

/** Stored instead of a country code while the user works across all of their countries. */
const ALL_COUNTRIES = 'ALL';

type CountryPreference = Country | typeof ALL_COUNTRIES;

/**
 * Holds the country the application is currently scoped to.
 *
 * Task owners can hold roles in several countries. Everything that differs per
 * country — the landing page tiles, the sidebar entries, the admin area — is
 * evaluated against the country selected here. Without a selection the user
 * works across all their countries at once, which is also the default. The
 * choice is persisted to {@link Window.localStorage} so it survives reloads and
 * is dropped as soon as the user no longer holds a role in that country.
 */
@Injectable({ providedIn: 'root' })
export class SelectedCountryService {
    private static readonly STORAGE_KEY = 'mdla:emea:selected-country';

    private readonly injector = inject(Injector);
    private readonly countrySwitchEnabled: Signal<boolean> = computed(() =>
        this.injector.get(EmeaFeatureService).isActive(EmeaFeature.COUNTRY_SWITCHER)
    );

    private readonly router = inject(Router);
    private readonly inAdminArea: Signal<boolean> = toSignal(
        this.router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd),
            map((event) => SelectedCountryService.isAdminUrl(event.urlAfterRedirects))
        ),
        { initialValue: SelectedCountryService.isAdminUrl(this.router.url) }
    );

    private readonly me: Signal<IUser | null> = select(UserState.me);
    private readonly preference = signal<CountryPreference | null>(this.restore());

    /**
     * Countries the user holds an active OM EMEA task owner role for. Admins who
     * also own tasks are limited to those countries, other admins get all of them.
     */
    public readonly countries: Signal<Country[]> = computed(() => {
        const user = this.me();
        const taskOwnerCountries = emeaTaskOwnerCountriesOf(user);
        return taskOwnerCountries.length > 0 ? taskOwnerCountries : emeaCountriesOf(user);
    });

    /** The country in scope, or `null` while all of the user's countries are in scope. */
    public readonly selected: Signal<Country | null> = computed(() => {
        const preferred = this.preference();
        return preferred && preferred !== ALL_COUNTRIES && this.countries().includes(preferred) ? preferred : null;
    });

    /** `true` when there is more than one country to switch between, outside of the admin area. */
    public readonly canSwitch: Signal<boolean> = computed(() => {
        return this.countries().length > 1 && this.countrySwitchEnabled() && !this.inAdminArea();
    });

    /**
     * Scopes the application to the given country, or to all of the user's
     * countries with `null`. Countries the user is not assigned to are ignored.
     */
    public select(country: Country | null): void {
        if (country && !this.countries().includes(country)) {
            return;
        }
        const preference = country ?? ALL_COUNTRIES;
        this.preference.set(preference);
        this.persist(preference);
    }

    private static isAdminUrl(url: string): boolean {
        return /^\/admin(?:[/?#]|$)/.test(url);
    }

    // Reading and writing localStorage can throw (e.g. quota, sandboxed iframes
    // or privacy settings); an unavailable storage just means no persistence.
    private persist(preference: CountryPreference): void {
        try {
            window.localStorage.setItem(SelectedCountryService.STORAGE_KEY, preference);
        } catch {
            // Ignore storage write failures.
        }
    }

    private restore(): CountryPreference | null {
        try {
            return (window.localStorage.getItem(SelectedCountryService.STORAGE_KEY) as CountryPreference) || null;
        } catch {
            return null;
        }
    }
}
